import { useEffect, useState } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import { SubjectType, type ApiSubject, type Subject } from '@/types/screening';
import { toError } from '@/utils/errors';

/** A company subject as the registry-document picker needs it: enough to
 * prefill a search and a manual order, nothing more. */
export interface RegistryOrderSubject {
  id: string;
  name: string;
  /** ISO alpha-2 codes with names, in the subject's own order. */
  jurisdictions: { code: string; name: string }[];
  registrationNumber: string | null;
  /** The first address line the subject carries, if any. */
  address: string | null;
}

/** Where the picker's subjects come from: the request's triage subjects
 * (while in triage) or the report's resolved subjects (after). */
export interface RegistryOrderSubjectSource {
  reportRequestId?: string | null;
  reportId?: string | null;
}

function readString(info: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = info?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function readFirstAddress(info: Record<string, unknown> | null | undefined): string | null {
  const value = info?.addresses;
  if (Array.isArray(value)) {
    const first = value.find((entry) => typeof entry === 'string' && entry.trim());
    return typeof first === 'string' ? first.trim() : null;
  }
  return readString(info, 'address');
}

function normalise(
  id: string,
  name: string,
  jurisdictions: { alpha2code: string; name: string }[] | null | undefined,
  info: Record<string, unknown> | null | undefined
): RegistryOrderSubject {
  return {
    id,
    name,
    jurisdictions: (jurisdictions ?? []).map((j) => ({
      code: j.alpha2code,
      name: j.name || j.alpha2code,
    })),
    // Only the flat, merged keys: `additional_info` also carries the audit
    // layers (original / llm_findings / user_overrides) behind them.
    registrationNumber: readString(info, 'registration_number'),
    address: readFirstAddress(info),
  };
}

export function toRegistryOrderSubjectFromApi(api: ApiSubject): RegistryOrderSubject {
  return normalise(api.id, api.subject_name, api.jurisdictions, api.additional_info);
}

export function toRegistryOrderSubject(subject: Subject): RegistryOrderSubject {
  return normalise(
    subject.id ?? '',
    subject.subjectName,
    subject.jurisdictions,
    subject.additionalInfo
  );
}

/**
 * The report's company subjects, for prefilling the registry-document
 * picker. Person subjects are left out: Armadillo sells company documents.
 *
 * `enabled` gates the fetch so a page does not read subjects until the
 * picker opens. A failed read is not fatal to ordering — the analyst can
 * still type the company in — so it surfaces as `error` for a quiet notice
 * rather than blocking anything.
 */
export function useRegistryOrderSubjects(source: RegistryOrderSubjectSource, enabled: boolean) {
  const [subjects, setSubjects] = useState<RegistryOrderSubject[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { reportRequestId, reportId } = source;

  useEffect(() => {
    if (!enabled) {
      // A closed picker keeps no list: the next open starts empty, so the
      // default subject waits for the fresh read instead of a stale one.
      setSubjects((previous) => (previous.length ? [] : previous));
      return;
    }
    if (!reportRequestId && !reportId) {
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    // Dropped before the read, not after: a picker opened twice must not
    // prefill from the previous open's list while this one is in flight.
    setSubjects([]);
    const load = reportRequestId
      ? vnextApiClient
          .listTriageSubjects(reportRequestId)
          .then((rows) =>
            rows
              .filter((row) => row.subject_type === SubjectType.Company)
              .map(toRegistryOrderSubjectFromApi)
          )
      : vnextApiClient
          .getReportSubjects(reportId as string)
          .then((rows) =>
            rows
              .filter((row) => row.subjectType === SubjectType.Company && row.id)
              .map(toRegistryOrderSubject)
          );
    load
      .then((rows) => {
        if (!cancelled) {
          setSubjects(rows);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const e = toError(err, 'Could not load the report subjects');
          Sentry.captureException(e);
          setError(e);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, reportRequestId, reportId]);

  return { subjects, isLoading, error };
}
